import { addDays, isAfter, isBefore } from 'date-fns';
import { type Program, type ProgramPostContentObjectList, type Topic, getMinContentDate } from 'models';
import { useTranslation } from 'react-i18next';
import {
  AttachmentsInput,
  type AttahcmentsInputUploadFile,
  Col,
  type DatePickerDateType,
  DateTimeInput,
  Form,
  FormActions,
  Loading,
  RichTextInput,
  SelectInput,
  TextInput,
  getDatePickerDateType,
  getDateType,
} from 'ui';
import { useProgramMentions } from '../../hooks';
import { useGetPostInitialValues } from './hooks';

export type PostFormValues = {
  attachments?: { fileList: AttahcmentsInputUploadFile[] };
  htmlContent: string;
  programTopicId: string;
  publishAt: DatePickerDateType;
  textContent?: string;
  title: string;
  todoDueDate: DatePickerDateType;
};

type P = {
  loading: boolean;
  mode: 'create' | 'edit';
  onBack?: () => void;
  onSubmit: (post: PostFormValues) => void;
  post?: ProgramPostContentObjectList;
  preSelectedProgramTopicId?: string;
  program: Program;
};

const PostForm = ({ loading, mode, onBack, onSubmit, post, preSelectedProgramTopicId, program }: P) => {
  const [form] = Form.useForm<PostFormValues>();
  const initialValues = useGetPostInitialValues({ postContentObject: post, preSelectedProgramTopicId, program });
  const handleOnRequestMentions = useProgramMentions(program.id);
  const { t } = useTranslation();

  const onFinish = (formValues: PostFormValues) => {
    onSubmit(formValues);
  };

  return loading ? (
    <Col style={{ fontSize: 24, textAlign: 'center' }}>
      <Loading />
    </Col>
  ) : (
    <Form<PostFormValues> form={form} initialValues={initialValues} onFinish={onFinish}>
      <SelectInput<Topic>
        input={{
          getOptionLabel: (option) => option.name,
          getOptionValue: (option) => option.id,
          options: program.topics,
          placeholder: t('Please select the module the post belongs to'),
        }}
        item={{
          'data-test-id': 'select_program-topic',
          label: 'Program module',
          name: 'programTopicId',
          rules: [
            {
              required: true,
            },
          ],
        }}
      />
      <DateTimeInput
        input={{
          minimum: getMinContentDate({ startTime: program.startTime }),
          showTime: true,
        }}
        item={{
          getValueFromEvent(newPublishDate: DatePickerDateType): DatePickerDateType {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Antd's FormInstance.getFieldValue returns any
            const currentDueDate: DatePickerDateType | undefined = form.getFieldValue('todoDueDate');

            if (currentDueDate && isBefore(getDateType(currentDueDate), getDateType(newPublishDate))) {
              form.setFieldsValue({ todoDueDate: getDatePickerDateType(addDays(getDateType(newPublishDate), 1)) });
            }

            return newPublishDate;
          },
          label: 'Publish date',
          name: 'publishAt',
          rules: [
            {
              required: true,
            },
            {
              message: t('Error: Post publish date must be set after Program start time'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                if (isBefore(getDateType(value), program.startTime)) {
                  throw new Error(t('Error: Post publish date must be set after Program start time'));
                }
              },
            },
            {
              message: t('Error: Post publish date must be set before Program end time'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                if (isAfter(getDateType(value), program.endTime)) {
                  throw new Error(t('Error: Post publish date must be set before Program end time'));
                }
              },
            },
          ],
        }}
      />
      <DateTimeInput
        input={{
          minimum: getMinContentDate({ startTime: program.startTime }),
          showTime: true,
        }}
        item={{
          dependencies: ['publishAt'],
          label: 'To-Do due date',
          name: 'todoDueDate',
          rules: [
            {
              required: true,
            },
            {
              message: t('Error: To-Do due date must be set after Program start time'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                if (isBefore(getDateType(value), program.startTime)) {
                  throw new Error(t('Error: To-Do due date must be set after Program start time'));
                }
              },
            },
            {
              message: t('Error: To-Do due date must be set before Program end time'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                if (isAfter(getDateType(value), program.endTime)) {
                  throw new Error(t('Error: To-Do due date must be set before Program end time'));
                }
              },
            },
            {
              message: t('Error: Due to date must be set after the publish date'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Antd's FormInstance.getFieldValue returns any
                const publishAt: DatePickerDateType | undefined = form.getFieldValue('publishAt');

                if (publishAt && isBefore(getDateType(value), getDateType(publishAt))) {
                  throw new Error(t('Error: Due to date must be set after the publish date'));
                }
              },
            },
          ],
        }}
      />
      <TextInput
        item={{
          label: 'Title',
          name: 'title',
          rules: [{ required: true }],
        }}
      />
      <RichTextInput
        html={{
          label: 'Content',
          mentions: handleOnRequestMentions,
          name: 'htmlContent',
          rules: [{ required: true }],
        }}
        text={{ name: 'textContent' }}
      />
      <AttachmentsInput item={{ label: 'Attachments', name: 'attachments' }} />
      <FormActions
        left={onBack ? { onClick: onBack } : undefined}
        submit={{ children: `${mode === 'edit' ? 'Edit' : 'Create'} Post` }}
      />
    </Form>
  );
};

export default PostForm;
